import React, { useMemo } from 'react';
import {
  Users, BookOpen, GraduationCap, Target, TrendingUp, Activity, Clock,
  UserPlus, Crown, MessageSquare, Heart, Radio, Calendar, Sparkles,
  Rocket, ArrowUpRight, Bot, Inbox, Gauge, Lightbulb, Zap, CheckCircle,
} from 'lucide-react';
import {
  getAdminGreeting,
  getAdminInsight,
  getHealthScore,
  ROLE_COLORS,
  PODIUM_MEDALS,
} from './adminOverviewUtils';
import './AdminOverviewPanel.css';

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatTime = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

const MOOD_EMOJI = ['✨', '🚀', '⚡', '🌟'];

const AdminOverviewPanel = ({
  dashboard,
  user,
  systemHealth,
  contactStats,
  healthBannerOk,
  onNavigate,
  inbox = [],
  crossLine = [],
  onRunTool,
  toolBusy,
}) => {
  const {
    overview = {},
    growth = {},
    usersByRole = {},
    recentUsers = [],
    recentActivity = [],
    topCourses = [],
    lines = {},
  } = dashboard || {};

  const adminName = user?.firstName || user?.name?.split(' ')?.[0] || 'Captain';
  const greeting = getAdminGreeting();
  const pendingActions =
    (overview.pendingInstructorApprovals || 0) + (overview.pendingCourseApprovals || 0);
  const unreadMessages = contactStats?.newMessages || 0;
  const healthScore = getHealthScore(systemHealth);
  const moodEmoji = MOOD_EMOJI[new Date().getDay() % MOOD_EMOJI.length];

  const insight = useMemo(
    () => getAdminInsight({ overview, growth, pendingActions, unreadMessages }),
    [overview, growth, pendingActions, unreadMessages]
  );

  const statTiles = [
    {
      key: 'users',
      value: (overview.totalUsers || 0).toLocaleString(),
      label: 'Total users',
      hint: `${overview.activeUsersWeek || 0} active this week`,
      icon: Users,
      color: '#22d3ee',
      delta: growth.userGrowth,
    },
    {
      key: 'enrollments',
      value: (overview.totalEnrollments || 0).toLocaleString(),
      label: 'Enrollments',
      hint: recentUsers.length ? 'Fresh faces joining' : 'Building momentum',
      icon: GraduationCap,
      color: '#34d399',
      delta: growth.enrollmentGrowth,
    },
    {
      key: 'courses',
      value: (overview.totalCourses || 0).toLocaleString(),
      label: 'Courses live',
      hint: `${overview.totalLessons || 0} lessons in catalog`,
      icon: BookOpen,
      color: '#f59e0b',
    },
    {
      key: 'completion',
      value: overview.completionRate != null ? `${overview.completionRate}%` : '—',
      label: 'Completion rate',
      hint: overview.completedEnrollments != null
        ? `${overview.completedEnrollments} finished`
        : 'Across all enrollments',
      icon: Target,
      color: '#8b5cf6',
    },
  ];

  const launchPad = [
    {
      label: 'Review courses',
      desc: 'Approvals & catalog',
      icon: BookOpen,
      color: '#f59e0b',
      tab: 'qoreacademy',
      subTab: 'courses',
      badge: overview.pendingCourseApprovals,
    },
    {
      label: 'Inbox',
      desc: 'Contact messages',
      icon: Inbox,
      color: '#22d3ee',
      tab: 'messages',
      badge: unreadMessages,
    },
    {
      label: 'Analytics',
      desc: 'Trends & reports',
      icon: TrendingUp,
      color: '#8b5cf6',
      tab: 'analytics',
    },
    {
      label: 'Nova AI',
      desc: 'Orchestrator',
      icon: Bot,
      color: '#34d399',
      tab: 'ai-orchestrator',
      subTab: 'nova',
    },
  ];

  const roleEntries = Object.entries(usersByRole);
  const roleTotal = roleEntries.reduce((s, [, c]) => s + c, 0);
  const maxEnrollments = topCourses[0]?.enrollments || 1;

  const goInsight = () => {
    onNavigate(insight.tab, insight.subTab);
  };

  return (
    <div className="ao-panel">
      {/* Hero bento */}
      <section className="ao-bento ao-bento--hero">
        <article className={`ao-insight ao-insight--${insight.tone}`}>
          <span className="ao-insight__badge">
            <Lightbulb size={12} />
            {insight.badge}
          </span>
          <h2 className="ao-insight__title">{insight.headline}</h2>
          <p className="ao-insight__body">{insight.body}</p>
          <button type="button" className="ao-insight__cta" onClick={goInsight}>
            {insight.cta}
            <ArrowUpRight size={15} />
          </button>
        </article>

        <article className="ao-hero">
          <div className="ao-hero__sparkles" aria-hidden>
            <span className="ao-hero__sparkle" />
            <span className="ao-hero__sparkle" />
            <span className="ao-hero__sparkle" />
          </div>
          <span className="ao-hero__pill">{greeting}, {adminName}</span>
          <h1>
            QoreNova is{' '}
            <span className="ao-hero__gradient">three lines, one command</span>
          </h1>
          <p>
            Academy · Talks · Connect — {(overview.totalUsers || 0).toLocaleString()} people on the platform.
            Nova is the coworker on this desk.
          </p>
          <div className="ao-hero__footer">
            <span className="ao-hero__date">
              <Calendar size={14} />
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })}
            </span>
            <span className="ao-hero__mood" aria-hidden>{moodEmoji}</span>
          </div>
        </article>
      </section>

      <section className="ao-bento ao-bento--stats ao-lines">
        {[
          {
            key: 'academy',
            label: 'QoreAcademy',
            color: '#f59e0b',
            tab: 'qoreacademy',
            value: (lines.academy?.publishedCourses ?? overview.totalCourses) || 0,
            hint: `${lines.academy?.pendingCourses || overview.pendingCourseApprovals || 0} courses pending · ${lines.academy?.completionRate ?? overview.completionRate ?? 0}% completion`,
          },
          {
            key: 'talks',
            label: 'QoreTalks',
            color: '#fb7185',
            tab: 'qoretalks',
            value: lines.talks?.published ?? 0,
            hint: `${lines.talks?.pendingGuests || 0} guest pitches · ${lines.talks?.liveNow || 0} live`,
          },
          {
            key: 'connect',
            label: 'QoreConnect',
            color: '#22d3ee',
            tab: 'qoreconnect',
            value: lines.connect?.forumActive ?? overview.totalForumPosts ?? 0,
            hint: `${lines.connect?.pendingMentors || 0} mentors pending · ${lines.connect?.forumFlagged || 0} flagged`,
          },
        ].map((line) => (
          <button
            key={line.key}
            type="button"
            className="ao-stat ao-stat--click"
            style={{ '--tile-color': line.color }}
            onClick={() => onNavigate(line.tab)}
          >
            <div className="ao-stat__label">{line.label}</div>
            <div className="ao-stat__value">{line.value}</div>
            <span className="ao-stat__hint">{line.hint}</span>
          </button>
        ))}
      </section>

      {(inbox.length > 0 || crossLine.length > 0) && (
        <section className="ao-bento ao-inbox-grid">
          <article className="ao-card">
            <div className="ao-card__head">
              <h3><Zap size={16} /> Action inbox</h3>
              <span className="ao-card__link">{inbox.length} open</span>
            </div>
            {inbox.length === 0 ? (
              <p className="ao-empty">Inbox clear — Nova has nothing gated.</p>
            ) : inbox.slice(0, 8).map((item) => (
              <div key={item.id} className="ao-queue__item">
                <span className="ao-queue__emoji">{item.line === 'talks' ? '🎙️' : item.line === 'connect' ? '🤝' : '📚'}</span>
                <div>
                  <strong>{item.title}</strong>
                  <div style={{ fontSize: '0.72rem', color: 'var(--admin-text-muted)' }}>{item.body}</div>
                </div>
                <div className="ao-inbox-actions">
                  {(item.tools || []).slice(0, 2).map((tool) => (
                    <button
                      key={tool.label}
                      type="button"
                      className="ao-queue__btn"
                      disabled={toolBusy === `${tool.name}:${JSON.stringify(tool.args)}`}
                      onClick={() => onRunTool?.(tool.name, tool.args, tool.label)}
                    >
                      {tool.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </article>
          <article className="ao-card">
            <div className="ao-card__head">
              <h3><Sparkles size={16} /> Cross-line</h3>
            </div>
            {crossLine.length === 0 ? (
              <p className="ao-empty">No guest→mentor or forum→talk plays right now.</p>
            ) : crossLine.slice(0, 6).map((s) => (
              <div key={s.id} className="ao-queue__item">
                <span className="ao-queue__emoji">↗</span>
                <div>
                  <strong>{s.title}</strong>
                  <div style={{ fontSize: '0.72rem', color: 'var(--admin-text-muted)' }}>{s.body}</div>
                </div>
                {s.sourceId && (
                  <button
                    type="button"
                    className="ao-queue__btn"
                    onClick={() => onRunTool?.('execute_cross_line', { type: s.type, sourceId: s.sourceId }, s.type)}
                  >
                    Run
                  </button>
                )}
              </div>
            ))}
          </article>
        </section>
      )}

      {/* Stat tiles */}
      <section className="ao-bento ao-bento--stats">
        {statTiles.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.key} className="ao-stat" style={{ '--tile-color': s.color }}>
              {s.delta != null && s.delta !== 0 && (
                <span className={`ao-stat__delta ${s.delta > 0 ? 'ao-stat__delta--up' : ''}`}>
                  {s.delta > 0 ? '+' : ''}{s.delta}%
                </span>
              )}
              <div className="ao-stat__icon">
                <Icon size={18} />
              </div>
              <div className="ao-stat__value">{s.value}</div>
              <div className="ao-stat__label">{s.label}</div>
              <span className="ao-stat__hint">{s.hint}</span>
            </div>
          );
        })}
      </section>

      {/* Mid row: health + launch + queue */}
      <section className="ao-bento ao-bento--mid">
        <article className="ao-health">
          <div
            className="ao-health__ring"
            style={{ '--pct': healthScore ?? (healthBannerOk ? 92 : 68) }}
          >
            <span className="ao-health__score">
              {healthScore != null ? healthScore : '—'}
            </span>
          </div>
          <h3>
            <Gauge size={16} style={{ verticalAlign: -2, marginRight: 4 }} />
            Platform pulse
          </h3>
          <p>{healthBannerOk ? 'Systems humming along' : 'A few metrics need eyes'}</p>
          <div className="ao-health__metrics">
            <div className="ao-health__metric">
              Uptime
              <strong>{systemHealth?.uptime?.formatted || '—'}</strong>
            </div>
            <div className="ao-health__metric">
              API
              <strong>
                {systemHealth?.apiResponseTime?.value != null
                  ? `${systemHealth.apiResponseTime.value}ms`
                  : '—'}
              </strong>
            </div>
            <div className="ao-health__metric">
              Database
              <strong>
                {systemHealth?.dbConnection?.status === 'ok' ? '✓ Connected' : 'Check'}
              </strong>
            </div>
            <div className="ao-health__metric">
              Memory
              <strong>
                {systemHealth?.memoryUsage?.percentage != null
                  ? `${systemHealth.memoryUsage.percentage}%`
                  : '—'}
              </strong>
            </div>
          </div>
          <button
            type="button"
            className="ao-insight__cta"
            style={{ marginTop: '0.25rem', fontSize: '0.78rem', padding: '0.4rem 0.75rem' }}
            onClick={() => onNavigate('system-health')}
          >
            Full health view
          </button>
        </article>

        <article className="ao-launch">
          <h3><Rocket size={18} /> Launch pad</h3>
          <div className="ao-launch__grid">
            {launchPad.map((item) => (
              <button
                key={item.label}
                type="button"
                className="ao-launch__btn"
                style={{ '--launch-color': item.color }}
                onClick={() => onNavigate(item.tab, item.subTab)}
              >
                <div className="ao-launch__icon">{<item.icon size={18} />}</div>
                <div>
                  <strong>{item.label}</strong>
                  <span>{item.desc}</span>
                </div>
                {item.badge > 0 && (
                  <span className="ao-launch__badge">{item.badge}</span>
                )}
                <ArrowUpRight size={14} style={{ marginLeft: item.badge > 0 ? 0 : 'auto', opacity: 0.5 }} />
              </button>
            ))}
          </div>
        </article>

        {pendingActions > 0 ? (
          <article className="ao-queue">
            <h3><Zap size={16} /> Needs you today</h3>
            {overview.pendingCourseApprovals > 0 && (
              <div className="ao-queue__item">
                <span className="ao-queue__emoji">📚</span>
                <div>
                  <strong>{overview.pendingCourseApprovals} course{overview.pendingCourseApprovals > 1 ? 's' : ''}</strong>
                  <div style={{ fontSize: '0.72rem', color: 'var(--admin-text-muted)' }}>Awaiting approval</div>
                </div>
                <button
                  type="button"
                  className="ao-queue__btn"
                  onClick={() => onNavigate('qoreacademy', 'courses')}
                >
                  Review
                </button>
              </div>
            )}
            {overview.pendingInstructorApprovals > 0 && (
              <div className="ao-queue__item">
                <span className="ao-queue__emoji">🎓</span>
                <div>
                  <strong>{overview.pendingInstructorApprovals} instructor{overview.pendingInstructorApprovals > 1 ? 's' : ''}</strong>
                  <div style={{ fontSize: '0.72rem', color: 'var(--admin-text-muted)' }}>Pending review</div>
                </div>
                <button
                  type="button"
                  className="ao-queue__btn"
                  onClick={() => onNavigate('qoreacademy', 'users')}
                >
                  Review
                </button>
              </div>
            )}
          </article>
        ) : (
          <article className="ao-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
            <CheckCircle size={32} style={{ color: '#34d399', marginBottom: '0.5rem' }} />
            <h3 style={{ margin: '0 0 0.35rem', fontSize: '0.95rem' }}>Queue is clear</h3>
            <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>
              No pending approvals — go build something fun.
            </p>
          </article>
        )}
      </section>

      {/* Roles + top courses */}
      <section className="ao-bento ao-bento--split">
        <article className="ao-card">
          <div className="ao-card__head">
            <h3><Users size={18} /> Who's on board</h3>
          </div>
          {roleEntries.length > 0 ? (
            <div className="ao-roles">
              {roleEntries.map(([role, count]) => {
                const pct = roleTotal > 0 ? (count / roleTotal) * 100 : 0;
                const color = ROLE_COLORS[role] || ROLE_COLORS.unknown;
                return (
                  <div key={role} className="ao-role-row">
                    <span className="ao-role-row__label">{role}s</span>
                    <div className="ao-role-row__track">
                      <div
                        className="ao-role-row__fill"
                        style={{ width: `${pct}%`, background: color }}
                      />
                    </div>
                    <strong>{count}</strong>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="ao-empty">No user roles yet.</p>
          )}
        </article>

        <article className="ao-card">
          <div className="ao-card__head">
            <h3><Crown size={18} /> Course champions</h3>
            <button type="button" className="ao-card__link" onClick={() => onNavigate('qoreacademy', 'courses')}>
              See all
            </button>
          </div>
          {topCourses.length > 0 ? (
            <div className="ao-podium">
              {topCourses.slice(0, 5).map((course, i) => (
                <div
                  key={course.id || i}
                  className={`ao-podium__item ${i === 0 ? 'ao-podium__item--gold' : ''}`}
                >
                  <span className="ao-podium__rank">
                    {i < 3 ? PODIUM_MEDALS[i] : `#${i + 1}`}
                  </span>
                  <div>
                    <p className="ao-podium__title">{course.title}</p>
                    <div className="ao-podium__meta">
                      {course.enrollments} enrolled
                      {course.completionRate > 0 && ` · ${Math.round(course.completionRate)}% progress`}
                    </div>
                  </div>
                  <div className="ao-podium__bar">
                    <div
                      className="ao-podium__fill"
                      style={{ width: `${Math.min((course.enrollments / maxEnrollments) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="ao-empty">No courses ranked yet — publish one!</p>
          )}
        </article>
      </section>

      {/* Activity + recent users */}
      <section className="ao-bento ao-bento--split">
        <article className="ao-card">
          <div className="ao-card__head">
            <h3><Clock size={18} /> Live feed</h3>
            <button type="button" className="ao-card__link" onClick={() => onNavigate('activity')}>
              Full log
            </button>
          </div>
          {recentActivity.length > 0 ? (
            <div className="ao-feed">
              {recentActivity.slice(0, 8).map((item, i) => (
                <div key={item.id || i} className="ao-feed__item">
                  <span className="ao-feed__dot" />
                  <div className="ao-feed__bubble">
                    <p className="ao-feed__action">{item.action || item.description}</p>
                    <div className="ao-feed__meta">
                      {item.user || 'System'} · {formatTime(item.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="ao-empty">It's quiet — activity will pop up here.</p>
          )}
        </article>

        <article className="ao-card">
          <div className="ao-card__head">
            <h3><UserPlus size={18} /> Fresh faces</h3>
            <button type="button" className="ao-card__link" onClick={() => onNavigate('qoreacademy', 'users')}>
              All users
            </button>
          </div>
          {recentUsers.length > 0 ? (
            <div className="ao-users">
              {recentUsers.slice(0, 6).map((u, i) => {
                const initial = (u.name || u.email || '?').charAt(0).toUpperCase();
                const roleColor = ROLE_COLORS[u.role] || ROLE_COLORS.unknown;
                return (
                  <div key={u.id || i} className="ao-user-row">
                    <div
                      className="ao-user-avatar"
                      style={{
                        background: `${roleColor}22`,
                        color: roleColor,
                        border: `1px solid ${roleColor}44`,
                      }}
                    >
                      {initial}
                    </div>
                    <div className="ao-user-info">
                      <p className="ao-user-name">{u.name || 'New member'}</p>
                      <p className="ao-user-email">{u.email}</p>
                    </div>
                    <span
                      className="ao-user-pill"
                      style={{ background: `${roleColor}22`, color: roleColor }}
                    >
                      {u.role}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="ao-empty">No new signups yet today.</p>
          )}
        </article>
      </section>

      {/* Community pulse strip */}
      <section className="ao-bento ao-bento--stats">
        {[
          {
            key: 'forum',
            icon: MessageSquare,
            value: overview.totalForumPosts != null ? overview.totalForumPosts.toLocaleString() : '—',
            label: 'Forum posts',
            color: '#22d3ee',
          },
          {
            key: 'mentors',
            icon: Heart,
            value: (overview.totalInstructors || 0).toLocaleString(),
            label: 'Instructors',
            color: '#fb7185',
          },
          {
            key: 'lessons',
            icon: Sparkles,
            value: (overview.totalLessons || 0).toLocaleString(),
            label: 'Lessons',
            color: '#8b5cf6',
          },
          {
            key: 'growth',
            icon: Activity,
            value: growth.usersThisMonth != null ? `+${growth.usersThisMonth}` : '—',
            label: 'Users this month',
            color: '#34d399',
          },
        ].map((chip) => {
          const Icon = chip.icon;
          return (
            <div key={chip.key} className="ao-stat" style={{ '--tile-color': chip.color }}>
              <div className="ao-stat__icon">
                <Icon size={18} />
              </div>
              <div className="ao-stat__value">{chip.value}</div>
              <div className="ao-stat__label">{chip.label}</div>
            </div>
          );
        })}
      </section>
    </div>
  );
};

export default AdminOverviewPanel;